import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { CalendarRange } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const CYCLES = [
  { v: 'weekly', l: 'Weekly (every 7 days)' },
  { v: 'biweekly', l: 'Bi-weekly (every 14 days)' },
  { v: 'semimonthly', l: 'Semi-monthly (1st–15th & 16th–end of month)' },
  { v: 'monthly', l: 'Monthly (calendar month)' },
];

export default function PayPeriodConfigDialog({ open, onClose, token, initial, onSaved }) {
  const { toast } = useToast();
  const [cycle, setCycle] = useState('biweekly');
  const [anchorEnd, setAnchorEnd] = useState('');
  const [callbackMin, setCallbackMin] = useState(3);
  const [callbackMult, setCallbackMult] = useState(1);
  const [winStart, setWinStart] = useState('18:00');
  const [winEnd, setWinEnd] = useState('07:00');
  const [useRegion, setUseRegion] = useState(false);
  const [regionTable, setRegionTable] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCycle(initial?.cycle || 'biweekly');
      setAnchorEnd(initial?.anchor_end || '');
      axios.get(`${API}/company/payroll-policy`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          setCallbackMin(r.data?.callback_min_hours ?? 3); setCallbackMult(r.data?.callback_rate_multiplier || 1);
          setWinStart(r.data?.callout_window_start || '18:00'); setWinEnd(r.data?.callout_window_end || '07:00');
          setUseRegion(!!r.data?.callout_use_region_table); setRegionTable(r.data?.callout_region_minimums || {});
        })
        .catch(() => {});
    }
  }, [open, initial, token]);

  const needsAnchor = cycle === 'weekly' || cycle === 'biweekly';

  const save = async () => {
    if (needsAnchor && !anchorEnd) {
      toast({ title: 'Cutoff date required', description: 'Pick the END date of any recent pay period.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await Promise.all([
        axios.put(
          `${API}/company/pay-period`,
          { cycle, anchor_end: needsAnchor ? anchorEnd : null },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.put(
          `${API}/company/payroll-policy`,
          { callback_min_hours: callbackMin || 0, callback_rate_multiplier: callbackMult || 1,
            callout_window_start: winStart, callout_window_end: winEnd,
            callout_use_region_table: useRegion, callout_region_minimums: regionTable },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      const { data } = await axios.get(`${API}/company/pay-period`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Pay period cycle saved', description: `Current period: ${data.current.start} → ${data.current.end}` });
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast({ title: 'Could not save', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pay-period-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-[#1E88E5]" /> Pay Period Cycle
          </DialogTitle>
          <DialogDescription>
            Define your payroll cutoff once — reports will open on the exact current pay period and offer "Last Pay Period" in one click.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Cycle</Label>
            <select
              value={cycle}
              onChange={(e) => setCycle(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm"
              data-testid="pay-period-cycle-select"
            >
              {CYCLES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          {needsAnchor && (
            <div className="space-y-1">
              <Label className="text-xs">A known cutoff date (end of any recent pay period)</Label>
              <Input
                type="date"
                value={anchorEnd}
                onChange={(e) => setAnchorEnd(e.target.value)}
                data-testid="pay-period-anchor-input"
              />
              <p className="text-[11px] text-slate-500">
                e.g. if your last period closed Friday June 12, pick 2026-06-12 — every future period repeats {cycle === 'weekly' ? '7' : '14'} days from it.
              </p>
            </div>
          )}
          <div className="space-y-1 pt-2 border-t">
            <label className="block text-sm font-medium">After-hours call-out window</label>
            <div className="flex items-center gap-2">
              <input type="time" value={winStart} onChange={(e) => setWinStart(e.target.value)} className="border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm" data-testid="callout-window-start-input" />
              <span className="text-xs text-slate-500">to</span>
              <input type="time" value={winEnd} onChange={(e) => setWinEnd(e.target.value)} className="border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm" data-testid="callout-window-end-input" />
            </div>
            <p className="text-[11px] text-slate-500">Punches that start inside this window pre-tick &quot;After-hours call-out&quot; for the worker (they can untick a planned night shift).</p>
          </div>
          <div className="space-y-1 pt-2 border-t">
            <label className="block text-sm font-medium" htmlFor="callback-min-input">Minimum call-out pay (hours)</label>
            <input
              id="callback-min-input"
              type="number"
              min="0"
              max="8"
              step="0.5"
              value={callbackMin}
              onChange={(e) => setCallbackMin(e.target.value === '' ? 0 : Math.max(0, Math.min(8, parseFloat(e.target.value) || 0)))}
              className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm"
              data-testid="callback-min-hours-input"
            />
            <p className="text-[11px] text-slate-500">
              Call-outs pay at least this many hours (Alberta: 3). Worked hours follow your normal daily/weekly overtime rules;
              the top-up to the minimum is regular pay. Applies to the workbench, Hours Review, payroll reports and QuickBooks pushes.
            </p>
            <label className="flex items-center gap-2 text-sm pt-1 cursor-pointer" data-testid="callout-use-region-toggle">
              <input type="checkbox" checked={useRegion} onChange={(e) => setUseRegion(e.target.checked)} className="w-4 h-4" />
              Use a per-province/state minimum from each worker&apos;s work region
            </label>
            {useRegion && (
              <div className="grid grid-cols-4 gap-1.5 pt-1 max-h-40 overflow-y-auto" data-testid="callout-region-table">
                {Object.keys(regionTable).sort().map((code) => (
                  <label key={code} className="flex items-center gap-1 text-xs">
                    <span className="w-7 font-mono text-slate-400">{code}</span>
                    <input type="number" min="0" max="8" step="0.5" value={regionTable[code]}
                      onChange={(e) => setRegionTable({ ...regionTable, [code]: e.target.value === '' ? 0 : Math.max(0, Math.min(8, parseFloat(e.target.value) || 0)) })}
                      className="w-14 border border-input bg-background text-foreground rounded px-1.5 py-1 text-xs" data-testid={`callout-region-${code}`} />
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1 pt-2 border-t">
            <label className="block text-sm font-medium" htmlFor="callback-mult-input">Callback rate premium (× multiplier)</label>
            <input
              id="callback-mult-input"
              type="number"
              min="1"
              max="3"
              step="0.25"
              value={callbackMult}
              onChange={(e) => setCallbackMult(e.target.value === '' ? 1 : Math.max(1, Math.min(3, parseFloat(e.target.value) || 1)))}
              className="w-full border border-input bg-background text-foreground rounded-md px-3 py-2 text-sm"
              data-testid="callback-rate-multiplier-input"
            />
            <p className="text-[11px] text-slate-500">
              Pay callback hours at a premium — e.g. 1.5 = time-and-a-half (1 = off). Added to gross pay in the workbench,
              payroll CSV and the worker&apos;s payday estimate. QuickBooks receives hours only, so apply the premium there as a pay item.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pay-period-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1E88E5] hover:bg-blue-700 text-white" data-testid="pay-period-save-btn">
            {saving ? 'Saving…' : 'Save Cycle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
